// src/views/Organizations/OrganizationsView.jsx
"use client";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import GlassPanel from "../../components/ui/GlassPanel";
import Spinner from "../../components/Spinner";
import { 
  Building2, 
  Users, 
  Settings, 
  Plus, 
  Trash2, 
  Shield,
  Crown,
  Eye,
  Edit,
  Mail,
  Globe,
  Calendar,
  ArrowLeft,
  Home,
  XCircle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useOrganizations } from "../../hooks/useOrganizations";
import AccountService from "../../services/account.service";
import OrganizationService from "../../services/organization.service";

/**
 * Organizations Management View
 * Gestión completa de organizaciones, members y proyectos
 * Integrado con OrganizationContext
 */
export default function OrganizationsView() {
  const navigate = useNavigate();
  const {
    organizations,
    organizationsLoading,
    personalAccount,
    createOrganization,
    updateOrganization,
    changeContext,
    error
  } = useOrganizations();
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loadingTier, setLoadingTier] = useState(false);

  // Cargar información del tier cuando el personalAccount esté disponible
  useEffect(() => {
    const loadTierInfo = async () => {
      if (personalAccount?.id) {
        console.log('🔍 Loading tier for account:', personalAccount.id);
        setLoadingTier(true);
        const result = await AccountService.getAccountTier(personalAccount.id);
        console.log('📊 Tier result:', result);
        if (result.success) {
          setTierInfo(result.data);
          console.log('✅ Tier loaded:', result.data);
        } else {
          console.error('❌ Failed to load tier:', result.error);
        }
        setLoadingTier(false);
      }
    };
    loadTierInfo();
  }, [personalAccount?.id]);

  const handleCreateOrg = async (formData) => {
    // Filtrar campos vacíos antes de enviar
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value && value.toString().trim() !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    console.log('📤 Sending organization data:', cleanedData);
    const result = await createOrganization(cleanedData);
    if (result.success) {
      setCreateModalOpen(false);
    }
  };

  const handleEditOrg = (organization) => {
    console.log('✏️ Editing organization:', organization);
    setSelectedOrg(organization);
    setEditModalOpen(true);
  };

  const handleUpdateOrg = async (formData) => {
    if (!selectedOrg) return;
    
    // Filtrar campos vacíos
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value && value.toString().trim() !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    console.log('📤 Updating organization:', selectedOrg.id, cleanedData);
    const result = await updateOrganization(selectedOrg.id, cleanedData);
    
    if (result.success) {
      console.log('✅ Organization updated successfully');
      setEditModalOpen(false);
      setSelectedOrg(null);
      window.location.reload();
    } else {
      alert('❌ Failed to update organization:\n' + (result.error?.join(', ') || 'Unknown error'));
    }
  };

  const handleDeleteOrg = async (orgId) => {
    if (window.confirm('⚠️ This will deactivate the organization. You can reactivate it later from settings.\n\nContinue?')) {
      console.log('🔄 Deactivating organization:', orgId);
      
      // Desactivar organización usando PUT con is_active: false
      const result = await updateOrganization(orgId, { is_active: false });
      
      console.log('Result:', result);
      
      if (result.success) {
        console.log('✅ Organization deactivated successfully');
        alert('✅ Organization deactivated successfully! The page will reload.');
        // Recargar la página para actualizar la lista
        window.location.reload();
      } else {
        console.error('❌ Failed to deactivate:', result.error);
        alert('❌ Failed to deactivate organization:\n' + (result.error?.join(', ') || 'Unknown error'));
      }
    }
  };

  const handleSelectOrg = (organization) => {
    console.log('🏢 Opening organization:', organization);
    // Cambiar el contexto a esta organización
    changeContext({
      type: 'ORGANIZATION',
      id: organization.id,
      name: organization.name
    });
    // Navegar al dashboard (home) con el nuevo contexto
    navigate('/');
  };

  return (
    <section className="min-h-screen bg-black p-8 opacity-0 animate-blur-reveal [animation-fill-mode:forwards]">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb / Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-all duration-300 mb-6 opacity-0 animate-reveal-left [animation-fill-mode:forwards] hover:translate-x-1"
          style={{ animationDelay: '0.1s' }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Dashboard</span>
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="opacity-0 animate-reveal-up [animation-fill-mode:forwards]" style={{ animationDelay: '0.15s' }}>
            <h1 className="text-3xl font-bold text-white mb-2">Organizations & Accounts</h1>
            <p className="text-gray-400">
              Manage your personal account, organizations, teams, and collaborative workspaces
            </p>
          </div>
          
          <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#7c3aed] hover:bg-[#6B3FC1] text-white transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 opacity-0 animate-reveal-right [animation-fill-mode:forwards]" style={{ animationDelay: '0.15s' }}>
                <Plus className="w-4 h-4 mr-2" />
                Create Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-white/20 text-white backdrop-blur-xl">
              <DialogHeader>
                <DialogTitle>Create New Organization</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Set up a new organization for your team
                </DialogDescription>
              </DialogHeader>
              <CreateOrganizationForm onSubmit={handleCreateOrg} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Personal Account Card */}
        <GlassPanel className="p-6 mb-6 border-[#7c3aed] opacity-0 animate-expand-in [animation-fill-mode:forwards]" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 
                            flex items-center justify-center flex-shrink-0">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-xl font-semibold text-white">
                    {personalAccount?.name || 'Personal Account'}
                  </h3>
                  <Badge className={`${
                    personalAccount?.is_active 
                      ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}>
                    {personalAccount?.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </Badge>
                </div>
                <p className="text-gray-400 text-sm mb-3">
                  {personalAccount?.description || 'Your private workspace for personal projects'}
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-400">
                      Created: <strong className="text-white">
                        {personalAccount?.created_at ? new Date(personalAccount.created_at).toLocaleDateString() : 'N/A'}
                      </strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-yellow-500" />
                    <span className="text-gray-400">
                      Tier: <strong className="text-white">
                        {loadingTier ? 'Loading...' : tierInfo ? (tierInfo.display_name || tierInfo.tier_type || 'N/A') : 'N/A'}
                      </strong>
                    </span>
                  </div>
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 w-fit">
                    {personalAccount?.type || 'PERSONAL'}
                  </Badge>
                </div>
              </div>
            </div>
            <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="bg-black/40 border-white/20 text-white hover:bg-white/5 flex-shrink-0"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0a0a] border-white/20 text-white max-w-2xl backdrop-blur-xl">
                <DialogHeader>
                  <DialogTitle>Account Settings</DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Manage your personal account settings
                  </DialogDescription>
                </DialogHeader>
                <AccountSettingsForm 
                  account={personalAccount}
                  tierInfo={tierInfo}
                  onClose={() => setSettingsModalOpen(false)} 
                />
              </DialogContent>
            </Dialog>
          </div>
        </GlassPanel>

        {/* Organizations List */}
        {organizationsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" color="purple" />
          </div>
        ) : error ? (
          <GlassPanel className="p-12 text-center">
            <p className="text-red-400">Error loading organizations: {error.join(', ')}</p>
          </GlassPanel>
        ) : organizations.length === 0 ? (
          <GlassPanel className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-xl font-semibold text-white mb-2">No organizations yet</h3>
            <p className="text-gray-400">
              Create your first organization to collaborate with your team
            </p>
          </GlassPanel>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {organizations.map((org) => (
              <OrganizationCard 
                key={org.id}
                organization={org}
                onDelete={handleDeleteOrg}
                onEdit={handleEditOrg}
                onSelect={handleSelectOrg}
              />
            ))}
          </div>
        )}
      </div>
      {/* Edit Organization Dialog */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/20 text-white backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update your organization details
            </DialogDescription>
          </DialogHeader>
          {selectedOrg && (
            <EditOrganizationForm 
              organization={selectedOrg}
              onSubmit={handleUpdateOrg}
            />
          )}
        </DialogContent>
      </Dialog>    </section>
  );
}

/**
 * Organization Card Component
 */
function OrganizationCard({ organization, onDelete, onEdit, onSelect }) {
  console.log('🏢 Organization card data:', organization);
  
  const [membersModalOpen, setMembersModalOpen] = React.useState(false);
  
  // Por defecto, si creaste la org, eres owner
  const role = organization.role || 'owner';
  const canManage = ['owner', 'admin'].includes(role);

  const getRoleIcon = (role) => {
    switch(role) {
      case 'owner': return <Crown className="w-4 h-4 text-purple-400" />;
      case 'admin': return <Shield className="w-4 h-4 text-blue-400" />;
      case 'member': return <Users className="w-4 h-4 text-gray-400" />;
      case 'viewer': return <Eye className="w-4 h-4 text-yellow-400" />;
      default: return <Users className="w-4 h-4 text-gray-400" />;
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      owner: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      member: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      viewer: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };

    return (
      <Badge className={`${roleColors[role] || roleColors.member}`}>
        <span className="mr-1">{getRoleIcon(role)}</span>
        {role?.toUpperCase()}
      </Badge>
    );
  };

  return (
    <GlassPanel className="p-6 hover:border-[#7c3aed] transition-colors">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-2xl">
              {organization.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{organization.name}</h3>
              <p className="text-sm text-gray-400">{organization.domain || 'No domain'}</p>
            </div>
          </div>
          {getRoleBadge(role)}
        </div>

        {/* Description */}
        {organization.description && (
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">
            {organization.description}
          </p>
        )}

        {/* Stats */}
        <div className="flex items-center gap-6 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="text-white">{organization.member_count || 0}</span>
            <span className="text-gray-400">members</span>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            <span className="text-white">{organization.project_count || 0}</span>
            <span className="text-gray-400">projects</span>
          </div>
        </div>

        {/* Meta Info */}
        <div className="space-y-2 mb-4 text-xs text-gray-400">
          {organization.contact_email && (
            <div className="flex items-center gap-2">
              <Mail className="w-3 h-3" />
              {organization.contact_email}
            </div>
          )}
          {organization.website && (
            <div className="flex items-center gap-2">
              <Globe className="w-3 h-3" />
              {organization.website}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            Created {new Date(organization.created_at).toLocaleDateString()}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-auto">
          <Button
            onClick={() => onSelect(organization)}
            className="flex-1 bg-[#7c3aed] hover:bg-[#6B3FC1] text-white"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Open
          </Button>
          <Button
            onClick={() => setMembersModalOpen(true)}
            variant="outline"
            className="bg-black/40 border-white/20 text-white hover:bg-white/5"
            title="View members"
          >
            <Users className="w-4 h-4" />
          </Button>
          {canManage && (
            <>
              <Button
                onClick={() => onEdit(organization)}
                variant="outline"
                className="bg-black/40 border-white/20 text-white hover:bg-white/5"
                title="Edit organization"
              >
                <Edit className="w-4 h-4" />
              </Button>
              {role === 'owner' && (
                <Button
                  onClick={() => onDelete(organization.id)}
                  variant="outline"
                  className="bg-black/40 border-white/20 text-orange-400 hover:bg-orange-500/20 hover:border-orange-500"
                  title="Deactivate organization"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Members Dialog */}
      <Dialog open={membersModalOpen} onOpenChange={setMembersModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/20 text-white max-w-3xl backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {organization.name} - Members
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Manage team members and their roles
            </DialogDescription>
          </DialogHeader>
          <OrganizationMembers 
            organizationId={organization.id}
            organizationName={organization.name}
            currentUserRole={role}
            onClose={() => setMembersModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </GlassPanel>
  );
}

/**
 * Create Organization Form Component
 */
function CreateOrganizationForm({ onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    description: '',
    contact_email: '',
    website: '',
    industry: '',
    size: '1-10',
    country: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-white mb-2 text-sm">Organization Name *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Acme Corporation"
          required
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">
          Domain
          <span className="text-gray-400 text-xs ml-2">(e.g., company.com - no http://)</span>
        </label>
        <Input
          value={formData.domain}
          onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
          placeholder="company.com"
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of your organization..."
          rows={3}
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-white mb-2 text-sm">Contact Email</label>
          <Input
            type="email"
            value={formData.contact_email}
            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
            placeholder="contact@acme.com"
            className="bg-black/40 border-white/20 text-white"
          />
        </div>

        <div>
          <label className="block text-white mb-2 text-sm">Company Size</label>
          <select
            value={formData.size}
            onChange={(e) => setFormData({ ...formData, size: e.target.value })}
            className="w-full px-3 py-2 bg-black/40 border border-white/20 rounded-lg text-white"
          >
            <option value="1-10">1-10 employees</option>
            <option value="11-50">11-50 employees</option>
            <option value="51-200">51-200 employees</option>
            <option value="201-500">201-500 employees</option>
            <option value="500+">500+ employees</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">Website</label>
        <Input
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          placeholder="https://acme.com"
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button 
          type="submit"
          className="flex-1 bg-[#7c3aed] hover:bg-[#6B3FC1] text-white"
        >
          Create Organization
        </Button>
      </div>
    </form>
  );
}

/**
 * Edit Organization Form Component
 */
function EditOrganizationForm({ organization, onSubmit }) {
  const [formData, setFormData] = useState({
    name: organization.name || '',
    domain: organization.domain || '',
    description: organization.description || '',
    contact_email: organization.contact_email || '',
    website: organization.website || '',
    industry: organization.industry || '',
    size: organization.size || '1-10',
    country: organization.country || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-white mb-2 text-sm">Organization Name *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Acme Corporation"
          required
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">
          Domain
          <span className="text-gray-400 text-xs ml-2">(e.g., company.com - no http://)</span>
        </label>
        <Input
          value={formData.domain}
          onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
          placeholder="company.com"
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of your organization..."
          rows={3}
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-white mb-2 text-sm">Contact Email</label>
          <Input
            type="email"
            value={formData.contact_email}
            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
            placeholder="contact@acme.com"
            className="bg-black/40 border-white/20 text-white"
          />
        </div>

        <div>
          <label className="block text-white mb-2 text-sm">Company Size</label>
          <select
            value={formData.size}
            onChange={(e) => setFormData({ ...formData, size: e.target.value })}
            className="w-full px-3 py-2 bg-black/40 border border-white/20 rounded-lg text-white"
          >
            <option value="1-10">1-10 employees</option>
            <option value="11-50">11-50 employees</option>
            <option value="51-200">51-200 employees</option>
            <option value="201-500">201-500 employees</option>
            <option value="500+">500+ employees</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-white mb-2 text-sm">Website</label>
        <Input
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          placeholder="https://acme.com"
          className="bg-black/40 border-white/20 text-white"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-white mb-2 text-sm">Industry</label>
          <Input
            value={formData.industry}
            onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
            placeholder="Technology"
            className="bg-black/40 border-white/20 text-white"
          />
        </div>

        <div>
          <label className="block text-white mb-2 text-sm">Country</label>
          <Input
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            placeholder="USA"
            className="bg-black/40 border-white/20 text-white"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button 
          type="submit"
          className="flex-1 bg-[#7c3aed] hover:bg-[#6B3FC1] text-white"
        >
          Update Organization
        </Button>
      </div>
    </form>
  );
}

/**
 * Account Settings Form Component
 */
function AccountSettingsForm({ account, tierInfo, onClose }) {
  const [formData, setFormData] = useState({
    name: account?.name || '',
    description: account?.description || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    const result = await AccountService.updateAccount(account.id, formData);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Account updated successfully!' });
      setTimeout(() => {
        window.location.reload(); // Recargar para ver cambios
      }, 1500);
    } else {
      setMessage({ type: 'error', text: result.error.join(', ') });
    }
    setLoading(false);
  };

  const handleSuspend = async () => {
    if (!window.confirm('Are you sure you want to suspend this account?')) return;
    
    setLoading(true);
    const result = await AccountService.suspendAccount(account.id);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Account suspended successfully!' });
      setTimeout(() => window.location.reload(), 1500);
    } else {
      setMessage({ type: 'error', text: result.error.join(', ') });
    }
    setLoading(false);
  };

  const handleReactivate = async () => {
    setLoading(true);
    const result = await AccountService.reactivateAccount(account.id);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Account reactivated successfully!' });
      setTimeout(() => window.location.reload(), 1500);
    } else {
      setMessage({ type: 'error', text: result.error.join(', ') });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Account Info */}
      <div className="bg-black/20 rounded-lg p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Account ID</span>
          <span className="text-xs text-white font-mono">{account?.id}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Type</span>
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            {account?.type}
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Status</span>
          <Badge className={`${
            account?.is_active 
              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
              : 'bg-red-500/20 text-red-400 border-red-500/30'
          }`}>
            {account?.is_active ? 'ACTIVE' : 'SUSPENDED'}
          </Badge>
        </div>
        {tierInfo && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Plan</span>
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-yellow-500" />
              <span className="text-white font-medium">{tierInfo.display_name || tierInfo.tier_type}</span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Created</span>
          <span className="text-white text-sm">
            {account?.created_at ? new Date(account.created_at).toLocaleDateString() : 'N/A'}
          </span>
        </div>
      </div>

      {/* Edit Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-white mb-2 text-sm font-medium">Account Name</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="My Personal Account"
            className="bg-black/40 border-white/20 text-white"
          />
        </div>

        <div>
          <label className="block text-white mb-2 text-sm font-medium">Description</label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Personal account for my projects..."
            rows={3}
            className="bg-black/40 border-white/20 text-white"
          />
        </div>

        {message && (
          <div className={`p-3 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {message.text}
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button 
            type="submit"
            disabled={loading}
            className="flex-1 bg-[#7c3aed] hover:bg-[#6B3FC1] text-white"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
          {account?.is_active ? (
            <Button 
              type="button"
              onClick={handleSuspend}
              disabled={loading}
              variant="outline"
              className="bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30"
            >
              Suspend Account
            </Button>
          ) : (
            <Button 
              type="button"
              onClick={handleReactivate}
              disabled={loading}
              variant="outline"
              className="bg-green-500/20 border-green-500/30 text-green-400 hover:bg-green-500/30"
            >
              Reactivate Account
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

/**
 * Organization Members Component
 */
function OrganizationMembers({ organizationId, organizationName, currentUserRole, onClose }) {
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('MEMBER');
  const [inviting, setInviting] = React.useState(false);
  const [message, setMessage] = React.useState(null);

  const canManage = ['owner', 'admin'].includes(currentUserRole?.toLowerCase());

  // Cargar miembros
  React.useEffect(() => {
    loadMembers();
  }, [organizationId]);

  const loadMembers = async () => {
    setLoading(true);
    const result = await OrganizationService.listMembers(organizationId);
    if (result.success) {
      setMembers(result.data);
    } else {
      setMessage({ type: 'error', text: result.error.join(', ') });
    }
    setLoading(false);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    setMessage(null);

    const result = await OrganizationService.addMember(organizationId, {
      email: inviteEmail,
      role: inviteRole
    });

    if (result.success) {
      setMessage({ type: 'success', text: 'Member invited successfully!' });
      setInviteEmail('');
      await loadMembers();
    } else {
      setMessage({ type: 'error', text: result.error.join(', ') });
    }
    setInviting(false);
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    const result = await OrganizationService.removeMember(organizationId, userId);
    if (result.success) {
      setMessage({ type: 'success', text: 'Member removed successfully!' });
      await loadMembers();
    } else {
      setMessage({ type: 'error', text: result.error.join(', ') });
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    const result = await OrganizationService.updateMemberRole(organizationId, userId, newRole);
    if (result.success) {
      setMessage({ type: 'success', text: 'Role updated successfully!' });
      await loadMembers();
    } else {
      setMessage({ type: 'error', text: result.error.join(', ') });
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      OWNER: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
      ADMIN: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
      MEMBER: 'text-gray-400 bg-gray-500/20 border-gray-500/30',
      VIEWER: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30'
    };
    return colors[role] || colors.MEMBER;
  };

  return (
    <div className="space-y-6">
      {/* Invite Form */}
      {canManage && (
        <form onSubmit={handleInvite} className="space-y-4 p-4 bg-black/20 rounded-lg border border-white/10">
          <h3 className="text-lg font-semibold text-white">Invite Member</h3>
          <div className="flex gap-3">
            <Input
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1 bg-black/40 border-white/20 text-white"
              required
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="bg-black/40 border border-white/20 text-white rounded-md px-3 py-2"
            >
              <option value="VIEWER">Viewer</option>
              <option value="MEMBER">Member</option>
              <option value="ADMIN">Admin</option>
            </select>
            <Button
              type="submit"
              disabled={inviting}
              className="bg-[#7c3aed] hover:bg-[#6B3FC1] text-white"
            >
              {inviting ? 'Inviting...' : 'Invite'}
            </Button>
          </div>
        </form>
      )}

      {/* Messages */}
      {message && (
        <div className={`p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
            : 'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {message.text}
        </div>
      )}

      {/* Members List */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          Members ({members.length})
        </h3>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" color="purple" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No members yet
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => (
              <div
                key={member.user_id}
                className="flex items-center justify-between p-4 bg-black/20 rounded-lg border border-white/10 hover:border-white/20 transition"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                    {member.user_name?.charAt(0).toUpperCase() || member.user_email?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="font-medium text-white">
                      {member.user_name || member.user_email}
                    </div>
                    <div className="text-sm text-gray-400">
                      {member.user_email}
                    </div>
                    {member.joined_at && (
                      <div className="text-xs text-gray-500">
                        Joined {new Date(member.joined_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {canManage && member.role?.toLowerCase() !== 'owner' ? (
                    <select
                      value={member.role?.toUpperCase()}
                      onChange={(e) => handleChangeRole(member.user_id, e.target.value)}
                      className="bg-black/40 border border-white/20 text-white rounded-md px-3 py-1.5 text-sm"
                    >
                      <option value="VIEWER">Viewer</option>
                      <option value="MEMBER">Member</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                  ) : (
                    <Badge className={getRoleColor(member.role?.toUpperCase())}>
                      {member.role?.toUpperCase()}
                    </Badge>
                  )}
                  
                  {canManage && member.role?.toLowerCase() !== 'owner' && (
                    <Button
                      onClick={() => handleRemoveMember(member.user_id)}
                      variant="outline"
                      size="sm"
                      className="bg-black/40 border-white/20 text-red-400 hover:bg-red-500/20 hover:border-red-500"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
